/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
// We know and accept the deeper cyclical dependencies here
import type { ReactElement } from 'react';
import { useState } from 'react';

import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { fillPlaceholders } from '@web-platform/global-components-utils/stringUtils';

import FooterLinkList from '../FooterLinkList';
import FooterLinkToggle from '../FooterLinkToggle';
import { mbdTrackingParams } from '../helpers/mbd';

import type { Strings, Link } from '../Footer';

import STYLES from './FooterLink.module.scss';

type Props = Link & {
  expanded: boolean;
  depth: number;
  strings: Pick<Strings, 'expand' | 'close'>;
};

const FooterLink = ({
  depth,
  expanded,
  href,
  id,
  links,
  rel,
  strings,
  target,
  text,
}: Props): ReactElement => {
  const [expandedState, setExpandedState] = useState<boolean>(false);

  const getLinkToggleAltText: () => string = () => {
    const altText = expanded ? strings.close : strings.expand;
    if (text === null) {
      return altText;
    }
    return fillPlaceholders(altText, { text });
  };

  const incrementDepth = depth + 1;
  return (
    <>
      <div className={STYLES['footer-link__link-expand']}>
        {href && href.length > 0 ? (
          <BpkLink
            id={id}
            aria-hidden={!expanded}
            tabIndex={expanded ? 0 : -1}
            className={STYLES['footer-link__link']}
            href={href}
            key={id}
            rel={rel}
            target={target}
            alternate
            implicit
            data-tracking-element-id={`footer_link_${id}`}
            data-tracking-common-params={mbdTrackingParams('footer_link', { link_id: id })}
          >
            {text}
          </BpkLink>
        ) : (
          <BpkText
            id={id}
            textStyle={TEXT_STYLES.sm}
            aria-hidden={!expanded}
            color={TEXT_COLORS.textOnDark}
            className={STYLES['footer-link__text']}
            data-tracking-element-id={`footer_link_${id}`}
            data-tracking-common-params={mbdTrackingParams('footer_link', { link_id: id })}
          >
            {text}
          </BpkText>
        )}
        {links && links.length > 0 ? (
          <FooterLinkToggle
            altText={getLinkToggleAltText()}
            expanded={expandedState}
            hidden={!expanded}
            key={`${id}-toggle`}
            onClick={() => setExpandedState(!expandedState)}
            data-tracking-element-id={`footer_link_toggle_${id}`}
            data-tracking-common-params={mbdTrackingParams('footer_link_toggle', {
              link_id: id,
            })}
          />
        ) : null}
      </div>
      {links && links.length > 0 ? (
        <div className={STYLES['footer-link__lists']} key={`${id}-list`}>
          <FooterLinkList
            expanded={expandedState}
            links={links}
            parentId={`${id}`}
            depth={incrementDepth}
            strings={strings}
          />
        </div>
      ) : null}
    </>
  );
};

export default FooterLink;
